<div class="flex w-full max-w-full items-center justify-center">
  <div class="flex w-full items-center justify-center gap-2 sm:hidden">
    <button
      type="button"
      (click)="onPrevious()"
      [disabled]="!canGoToPrevious"
      class="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border transition-all"
      [class.bg-blue-75]="canGoToPrevious"
      [class.border-blue-700]="canGoToPrevious"
      [class.text-blue-700]="canGoToPrevious"
      [class.hover:bg-blue-700]="canGoToPrevious"
      [class.hover:text-white]="canGoToPrevious"
      [attr.cursor-pointer]="canGoToPrevious"
      [class.bg-gray-50]="!canGoToPrevious"
      [class.border-gray-500]="!canGoToPrevious"
      [class.text-gray-500]="!canGoToPrevious"
      [class.cursor-not-allowed]="!canGoToPrevious"
      [attr.aria-label]="'Go to previous page'"
      [attr.aria-disabled]="!canGoToPrevious"
    >
      <mat-icon>chevron_left</mat-icon>
    </button>

    <span
      class="font-lato flex h-10 min-w-28 items-center justify-center rounded-sm border border-gray-300 bg-white px-3 text-sm font-semibold"
      aria-live="polite"
    >
      Page {{ currentPage }} of {{ totalPages }}
    </span>

    <button
      type="button"
      (click)="onNext()"
      [disabled]="!canGoToNext"
      class="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border transition-all"
      [class.bg-blue-75]="canGoToNext"
      [class.border-blue-700]="canGoToNext"
      [class.text-blue-700]="canGoToNext"
      [class.hover:bg-blue-700]="canGoToNext"
      [class.hover:text-white]="canGoToNext"
      [attr.cursor-pointer]="canGoToNext"
      [class.bg-gray-50]="!canGoToNext"
      [class.border-gray-500]="!canGoToNext"
      [class.text-gray-500]="!canGoToNext"
      [class.cursor-not-allowed]="!canGoToNext"
      [attr.aria-label]="'Go to next page'"
      [attr.aria-disabled]="!canGoToNext"
    >
      <mat-icon>chevron_right</mat-icon>
    </button>
  </div>

  <div class="hidden items-center justify-center gap-2 sm:flex">
    <!-- Previous Button -->
    <button
      type="button"
      (click)="onPrevious()"
      [disabled]="!canGoToPrevious"
      class="font-lato flex h-10 min-w-16 items-center justify-center rounded-sm border px-3 py-2 text-sm font-bold transition-all"
      [class.bg-blue-75]="canGoToPrevious"
      [class.border-blue-700]="canGoToPrevious"
      [class.text-blue-700]="canGoToPrevious"
      [class.hover:bg-blue-700]="canGoToPrevious"
      [class.hover:text-white]="canGoToPrevious"
      [attr.cursor-pointer]="canGoToPrevious"
      [class.bg-gray-50]="!canGoToPrevious"
      [class.border-gray-500]="!canGoToPrevious"
      [class.text-gray-500]="!canGoToPrevious"
      [class.cursor-not-allowed]="!canGoToPrevious"
      [attr.aria-label]="'Go to previous page'"
      [attr.aria-disabled]="!canGoToPrevious"
    >
      <mat-icon class="mr-1">chevron_left</mat-icon>
      Previous
    </button>

    <!-- Page Numbers -->
    <div class="flex items-center gap-2">
      <!-- First page if not visible -->
      @if (showLeftEllipsis && visiblePages[0] > 1) {
        <button
          type="button"
          (click)="onPageClick(1)"
          class="hover:bg-blue-75 text-brand-muted flex h-10 min-w-10 cursor-pointer items-center justify-center rounded-sm border border-gray-300 bg-white px-2 py-2 text-sm font-semibold transition-all hover:border-blue-700 hover:text-blue-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700"
        >
          1
        </button>
        <span class="text-black">...</span>
      }

      <!-- Visible page numbers -->
      @for (page of visiblePages; track page) {
        <button
          type="button"
          (click)="onPageClick(page)"
          [attr.aria-current]="page === currentPage ? 'page' : null"
          class="flex h-10 min-w-10 cursor-pointer items-center justify-center rounded-sm px-2 py-2 text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700"
          [class.bg-blue-700]="page === currentPage"
          [class.border]="page === currentPage"
          [class.border-blue-700]="page === currentPage"
          [class.text-white]="page === currentPage"
          [class.hover:bg-blue-400]="page === currentPage"
          [class.hover:border-blue-400]="page === currentPage"
          [class.border]="page !== currentPage"
          [class.border-black]="page !== currentPage"
          [class.bg-white]="page !== currentPage"
          [class.text-black]="page !== currentPage"
          [class.hover:border-blue-700]="page !== currentPage"
          [class.hover:bg-blue-75]="page !== currentPage"
          [class.hover:text-blue-900]="page !== currentPage"
        >
          {{ page }}
        </button>
      }

      <!-- Last page if not visible -->
      @if (
        showRightEllipsis && visiblePages[visiblePages.length - 1] < totalPages
      ) {
        <span class="text-black">...</span>
        <button
          type="button"
          (click)="onPageClick(totalPages)"
          class="hover:bg-blue-75 flex h-10 min-w-10 cursor-pointer items-center justify-center rounded-sm border border-black bg-white px-2 py-2 text-sm font-semibold text-black transition-all hover:border-blue-700 hover:text-blue-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700"
        >
          {{ totalPages }}
        </button>
      }
    </div>

    <!-- Next Button -->
    <button
      type="button"
      (click)="onNext()"
      [disabled]="!canGoToNext"
      class="font-lato flex h-10 min-w-16 cursor-pointer items-center justify-center rounded-sm border px-3 py-2 text-sm font-bold transition-all"
      [class.bg-blue-75]="canGoToNext"
      [class.border-blue-700]="canGoToNext"
      [class.text-blue-700]="canGoToNext"
      [class.hover:bg-blue-700]="canGoToNext"
      [class.hover:text-white]="canGoToNext"
      [attr.cursor-pointer]="canGoToNext"
      [class.bg-gray-50]="!canGoToNext"
      [class.border-gray-500]="!canGoToNext"
      [class.text-gray-500]="!canGoToNext"
      [class.cursor-not-allowed]="!canGoToNext"
      [attr.aria-label]="'Go to next page'"
      [attr.aria-disabled]="!canGoToNext"
    >
      Next
      <mat-icon class="ml-1">chevron_right</mat-icon>
    </button>
  </div>
</div>
